import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { UpdateAdicionalAction } from '@/application/actions';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button, Input } from '@/presentation/components/common';
import { Adicional, ADICIONAL_TIPO_LABELS, type AdicionalTipo } from '@/domain/entities/Adicional';
import type { AdicionalFormData } from '@/domain/repositories/IAdicionalRepository';
import {
  defaultPackName,
  packDescriptionPlaceholder,
  packHelpText,
  packQuantityLabel,
} from './adicionalModalCopy';

interface EditAdicionalModalProps {
  isOpen: boolean;
  adicional: Adicional;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditAdicionalModal: React.FC<EditAdicionalModalProps> = ({
  isOpen,
  adicional,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<AdicionalFormData>({
    nombre: adicional.nombre,
    descripcion: adicional.descripcion,
    precioUnitario: adicional.precioUnitario,
    unidad: adicional.unidad,
    tipo: adicional.tipo,
    cupoMensajes: adicional.cupoMensajes,
    esRecurrente: false,
    activo: adicional.activo,
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        nombre: adicional.nombre,
        descripcion: adicional.descripcion,
        precioUnitario: adicional.precioUnitario,
        unidad: adicional.unidad,
        tipo: adicional.tipo,
        cupoMensajes: adicional.cupoMensajes,
        esRecurrente: false,
        activo: adicional.activo,
      });
    }
  }, [isOpen, adicional]);

  const isPack =
    formData.tipo === 'WHATSAPP_MENSAJES' || formData.tipo === 'EMAIL_MENSAJES';
  const isConexion = formData.tipo === 'COOFIT_CONEXION';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      showError(t('pages.adicionales.validation.nameRequired'));
      return;
    }
    if (formData.precioUnitario < 0) {
      showError(t('pages.adicionales.validation.priceMin'));
      return;
    }
    if ((isPack || isConexion) && (!formData.cupoMensajes || formData.cupoMensajes < 1)) {
      showError('Indica cuantos items incluye el pack');
      return;
    }

    setIsLoading(true);
    try {
      const action = container.get<UpdateAdicionalAction>(TYPES.UpdateAdicionalAction);
      await action.execute(adicional.id, {
        ...formData,
        esRecurrente: formData.tipo !== 'WHATSAPP_MENSAJES',
        cupoMensajes: formData.cupoMensajes,
      });
      success(isConexion ? t('pages.coofitConexion.updatedSuccess') : t('pages.adicionales.updatedSuccess'));
      onSuccess();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Error al actualizar adicional');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500/70" onClick={onClose} />
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="px-5 pt-5 pb-4 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isConexion
                    ? t('pages.coofitConexion.edit')
                    : t('pages.adicionales.edit')}{' '}
                  — {adicional.nombre}
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 text-xl">
                  ×
                </button>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo</p>
                {isConexion ? (
                  <p className="rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/80 dark:bg-teal-950/30 px-3 py-2 text-sm text-teal-900 dark:text-teal-100">
                    {ADICIONAL_TIPO_LABELS.COOFIT_CONEXION} — {packHelpText('COOFIT_CONEXION')}
                  </p>
                ) : (
                <div className="grid grid-cols-2 gap-2">
                  {(['WHATSAPP_MENSAJES', 'EMAIL_MENSAJES'] as AdicionalTipo[]).map(
                    (tipo) => (
                      <button
                        key={tipo}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            tipo,
                            cupoMensajes: prev.cupoMensajes || 500,
                            unidad: 'pack',
                            nombre:
                              prev.nombre.trim() === '' ||
                              prev.nombre === defaultPackName('WHATSAPP_MENSAJES') ||
                              prev.nombre === defaultPackName('EMAIL_MENSAJES')
                                ? defaultPackName(tipo)
                                : prev.nombre,
                          }))
                        }
                        className={`rounded-lg border px-2 py-2 text-sm ${
                          formData.tipo === tipo
                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/40'
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        {ADICIONAL_TIPO_LABELS[tipo]}
                      </button>
                    )
                  )}
                </div>
                )}
                {!isConexion && (
                  <p className="text-xs text-gray-500 mt-1.5">{packHelpText(formData.tipo)}</p>
                )}
              </div>

              <Input
                label={t('pages.adicionales.name')}
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder={defaultPackName(formData.tipo) || t('pages.adicionales.namePlaceholder')}
                maxLength={100}
                required
              />

              {isPack && (
                <Input
                  label={packQuantityLabel(formData.tipo)}
                  type="number"
                  min="1"
                  value={formData.cupoMensajes ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      cupoMensajes: parseInt(e.target.value, 10) || null,
                    })
                  }
                  required
                />
              )}
              {isConexion ? (
                <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-300">
                  Sin cupo de mensajes: el volumen se define con packs WhatsApp por clínica.
                </p>
              ) : null}

              <Input
                label={isPack || isConexion ? 'Precio mensual' : t('pages.adicionales.unitPrice')}
                type="number"
                step="0.01"
                min="0"
                value={formData.precioUnitario}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    precioUnitario: parseFloat(e.target.value) || 0,
                  })
                }
                required
              />

              {!isPack && !isConexion && (
                <Input
                  label={t('pages.adicionales.unit')}
                  type="text"
                  value={formData.unidad}
                  onChange={(e) => setFormData({ ...formData, unidad: e.target.value })}
                  maxLength={50}
                />
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('pages.adicionales.description')}
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  rows={2}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  maxLength={500}
                  placeholder={isPack ? packDescriptionPlaceholder(formData.tipo) : undefined}
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                    className="rounded"
                  />
                  {t('pages.adicionales.active')}
                </label>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-gray-900/50 px-5 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" isLoading={isLoading}>
                {t('common.save')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
